import { useEffect } from "react";
import { Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { store } from "./store/store";
import theme from "./styles/theme";
import Layout from "./components/Layout/Layout";
import {
  initAnalytics,
  trackPageView,
  trackException,
} from "./utils/analytics";

// Pages are imported statically (no React.lazy): prerendered HTML must match
// the first client render exactly, and lazy routes would hydrate against a
// Suspense fallback instead of the snapshot — breaking hydration on every page.
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import FAQ from "./pages/FAQ";
import NotFound from "./pages/NotFound";

// Wrapper component that includes the Layout and renders the child routes
const LayoutWrapper = () => {
  return (
    <>
      <Layout>
        <Outlet />
      </Layout>
    </>
  );
};

const AnalyticsTracker = () => {
  const { pathname } = useLocation();

  // Initialize analytics and set up error boundary on component mount
  useEffect(() => {
    initAnalytics();

    // Set up error boundary reporting
    const handleError = (event: ErrorEvent) => {
      trackException(event.message, true);
    };

    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("error", handleError);
    };
  }, []);

  // Track page views on initial load and route changes
  useEffect(() => {
    trackPageView(pathname, document.title);
  }, [pathname]);

  return null;
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HelmetProvider>
          <AnalyticsTracker />
          <Routes>
            <Route element={<LayoutWrapper />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:slug" element={<ProductDetail />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </HelmetProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
